import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotBoard from "../board/ScreenshotBoard";
import { useBoardMatColor } from "../board/useBoardMatColor";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import {
    getImageUrl,
    getTemplateGrid,
    loadScreenshotJigsaw,
} from "../lib/api";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { getScreenshotScene } from "../screenshotScenes";
import { JIGSAW_APP_ICON_ART } from "./AppIconPage";
import { FEATURE_GRAPHIC_PATH, JIGSAW_MOBILE_TITLE } from "../routes";
import { jigsawMobileAccent } from "../styles/mainStyles";

/** Google Play Feature Graphic canvas size. */
export const FEATURE_GRAPHIC_W = 1024;
export const FEATURE_GRAPHIC_H = 500;

const SAVE_BAR_H = 64;
const FEATURE_GRAPHIC_FILE_NAME = "jigsaw-muse-feature-graphic-1024x500.png";
const PUBLIC = process.env.PUBLIC_URL || "";
const FEATURE_GRAPHIC_ART = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;

/** Same Ocean puzzle as `/screenshot/06` (Daily). */
const FEATURE_GRAPHIC_SCENE_SLUG = "06";

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';

/**
 * Google Play Feature Graphic — 1024×500.
 * Left: app icon + Jigsaw Muse name. Right: full Ocean puzzle from screenshot/06.
 */
export default function FeatureGraphicPage() {
    const scene = getScreenshotScene(FEATURE_GRAPHIC_SCENE_SLUG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);
    const { mat: boardMat, grooveStyle: boardGrooveStyle } = useBoardMatColor();

    const expired = isScreenshotSceneExpired(FEATURE_GRAPHIC_SCENE_SLUG);

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        if (!scene?.storyId) {
            setError("Feature graphic scene is not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotJigsaw(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, scene]);

    const imageUrl = useMemo(() => getImageUrl(group?.story), [group]);
    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const paths = template?.piecePaths?.pieces ?? template?.piecePaths;
    const pieceList = Array.isArray(paths) ? paths : paths?.pieces;
    const total = grid > 0 ? grid * grid : 0;

    // Show the full assembled puzzle (all pieces on board).
    const boardPieceIndices = useMemo(
        () => (total > 0 ? Array.from({ length: total }, (_, i) => i) : []),
        [total],
    );

    const ready = Boolean(imageUrl && grid > 0 && pieceList?.length);

    useEffect(() => {
        if (expired || loading || error || !ready) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: FEATURE_GRAPHIC_PATH,
            slug: "feature-graphic",
            imageUrls: [imageUrl, JIGSAW_APP_ICON_ART].filter(Boolean),
        });
        return undefined;
    }, [expired, loading, error, ready, imageUrl]);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / FEATURE_GRAPHIC_W, availableH / FEATURE_GRAPHIC_H),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: FEATURE_GRAPHIC_W,
                height: FEATURE_GRAPHIC_H,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = FEATURE_GRAPHIC_FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    // Right board: leave a little margin; keep square fully visible in 500px height.
    const boardSide = 440;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#1a1028",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(FEATURE_GRAPHIC_W * previewScale),
                    height: Math.round(FEATURE_GRAPHIC_H * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="jigsaw-feature-graphic"
                    sx={{
                        width: FEATURE_GRAPHIC_W,
                        height: FEATURE_GRAPHIC_H,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#1a1028",
                        boxShadow: "0 0 0 1px rgba(167, 139, 250, 0.25)",
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: `
                                radial-gradient(ellipse 50% 70% at 18% 45%, rgba(124, 58, 237, 0.35) 0%, transparent 65%),
                                radial-gradient(ellipse 45% 60% at 82% 55%, rgba(59, 166, 102, 0.18) 0%, transparent 60%),
                                linear-gradient(115deg, #12081f 0%, #2a1548 42%, #1a1028 70%, #0c0614 100%)
                            `,
                        }}
                    />

                    {/* Left: app icon + name */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 440,
                            zIndex: 5,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2.5,
                            px: 4,
                            boxSizing: "border-box",
                            pointerEvents: "none",
                        }}
                    >
                        <Box
                            component="img"
                            src={JIGSAW_APP_ICON_ART}
                            alt=""
                            sx={{
                                width: 168,
                                height: 168,
                                borderRadius: "36px",
                                objectFit: "cover",
                                display: "block",
                                boxShadow:
                                    "0 12px 40px rgba(0,0,0,0.55), 0 0 0 2px rgba(250, 204, 21, 0.35), 0 0 28px rgba(167, 139, 250, 0.35)",
                                userSelect: "none",
                            }}
                        />
                        <Typography
                            component="h1"
                            sx={{
                                m: 0,
                                fontFamily: TITLE_STACK,
                                fontWeight: 900,
                                fontSize: 42,
                                lineHeight: 1.05,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                textAlign: "center",
                                background:
                                    "linear-gradient(180deg, #fef9c3 0%, #facc15 35%, #eab308 70%, #fde68a 100%)",
                                WebkitBackgroundClip: "text",
                                backgroundClip: "text",
                                color: "transparent",
                                filter:
                                    "drop-shadow(0 2px 8px rgba(0,0,0,0.65)) drop-shadow(0 0 18px rgba(250, 204, 21, 0.35))",
                                userSelect: "none",
                            }}
                        >
                            {JIGSAW_MOBILE_TITLE}
                        </Typography>
                    </Box>

                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={36} sx={{ color: jigsawMobileAccent }} />
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 4,
                            }}
                        >
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        </Box>
                    ) : ready ? (
                        <Box
                            sx={{
                                position: "absolute",
                                right: 32,
                                top: "50%",
                                width: boardSide,
                                height: boardSide,
                                transform: "translateY(-50%)",
                                zIndex: 2,
                            }}
                        >
                            <Box
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    inset: -20,
                                    borderRadius: 4,
                                    background:
                                        "radial-gradient(circle at 50% 45%, rgba(59,166,102,0.28) 0%, rgba(167,139,250,0.12) 45%, transparent 70%)",
                                    filter: "blur(8px)",
                                    zIndex: 0,
                                }}
                            />
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "16px",
                                    p: "8px",
                                    background:
                                        "linear-gradient(145deg, rgba(250,204,21,0.45) 0%, rgba(42,21,72,0.95) 40%, rgba(59,166,102,0.4) 100%)",
                                    boxShadow:
                                        "0 0 0 1px rgba(253,230,138,0.35), 0 24px 48px rgba(0,0,0,0.55), 0 0 48px rgba(124,58,237,0.25)",
                                    zIndex: 1,
                                    boxSizing: "border-box",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        bgcolor: "#1a1028",
                                        boxShadow: "inset 0 0 0 2px rgba(59,166,102,0.35)",
                                    }}
                                >
                                    <ScreenshotBoard
                                        grid={grid}
                                        imageUrl={imageUrl}
                                        paths={pieceList}
                                        boardPieceIndices={boardPieceIndices}
                                        svgPathFallback={template.svgPath}
                                        matPreset={boardMat}
                                        matColor={boardMat.hex}
                                        grooveStyle={boardGrooveStyle}
                                        fullBleed
                                        fillHeight
                                        showFullImage
                                        fullImageFit="cover"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">Puzzle image missing.</Typography>
                        </Box>
                    )}
                    <Box
                        component="img"
                        src={FEATURE_GRAPHIC_ART}
                        alt="Jigsaw Muse feature graphic"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                            display: "block",
                            zIndex: 50,
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    minHeight: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    py: 1,
                    bgcolor: "rgba(26, 16, 40, 0.94)",
                    borderTop: "1px solid rgba(167, 139, 250, 0.25)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.7)" }}>
                    Jigsaw Muse · Feature graphic · {FEATURE_GRAPHIC_W}×{FEATURE_GRAPHIC_H} · PNG
                </Typography>
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: jigsawMobileAccent,
                        "&:hover": { bgcolor: "#2d8a52" },
                    }}
                >
                    {saving ? "Saving…" : "Download 1024×500 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
